import type { Branded } from 'types';
import { mapValues } from 'utils/collection';
import type { RequestOptions } from 'utils/fetch-new';
import { formatTemplate } from 'utils/format';
import { is } from 'utils/helpers';
import { createUrl, joinPath, type URLParams } from 'utils/url';

export type HttpMethod = 'DELETE' | 'GET' | 'POST' | 'PUT';

/**
 * Describe what an endpoint takes and answers with.
 *
 * `path` can contain `{{pathParam}}` placeholders, one for each path param.
 *
 * `response: Model` means the endpoint always answers with data and an empty
 * response is an error. Use `response: Model | undefined` for an endpoint
 * that can answer with no data, and `response: undefined` for one that never
 * has any. Don't make it optional, `response?: Model` resolves to no data.
 */
export interface EndpointSpec {
	body?: unknown;
	path: string;
	pathParams?: Record<string, number | string>;
	// Query param interfaces rarely have an index signature, so URLParams
	// can't be the constraint here. The URL builder treats them as such.
	queryParams?: object;
	response?: unknown;
}

interface HttpMethodPrefix {
	DELETE: 'Delete';
	GET: 'Get';
	POST: 'Post';
	PUT: 'Put';
}

type EndpointMethod<Name> = {
	[M in HttpMethod]: Name extends `${HttpMethodPrefix[M]}${string}` ? M : never;
}[HttpMethod];

/**
 * Map endpoint names to their specs.
 *
 * Every name starts with its method, `GetCart` or `PutList`.
 *
 * @example
 *
 * interface StoreEndpoints {
 *   GetStore: {
 *     path: 'Stores/{{storeId}}';
 *     pathParams: { storeId: StoreId };
 *     response: Store;
 *   };
 *   GetStores: { path: 'Stores'; response: Store[] };
 * }
 */
export type EndpointMap<E> = {
	[K in keyof E]: [EndpointMethod<K>] extends [never] ? never : EndpointSpec;
};

type EndpointName<E> = keyof E & string;

type AllowEmpty<S> = S extends { response: infer R }
	? undefined extends R
		? true
		: false
	: true;

/**
 * Define how every endpoint in a map is called.
 *
 * @example
 *
 * const storeEndpoints: EndpointDefinitions<StoreEndpoints> = {
 *   GetStore: { allowEmpty: false, method: 'GET', path: 'Stores/{{storeId}}' },
 *   GetStores: { allowEmpty: false, method: 'GET', path: 'Stores' },
 * };
 */
export type EndpointDefinitions<E extends EndpointMap<E>> = {
	[K in keyof E]: {
		allowEmpty: AllowEmpty<E[K]>;
		method: EndpointMethod<K>;
		path: E[K]['path'];
	};
};

type EndpointParamKey = 'body' | 'pathParams' | 'queryParams';

type EndpointParams<S, Keys extends EndpointParamKey> = Pick<S, Keys & keyof S>;

type EndpointInputs<E extends EndpointMap<E>, Keys extends EndpointParamKey> = {
	[K in EndpointName<E>]:
		| ({ name: K } & EndpointParams<E[K], Keys>)
		| (Partial<EndpointParams<E[K], Keys>> extends EndpointParams<E[K], Keys>
				? K
				: never);
}[EndpointName<E>];

/**
 * Type the input for an endpoint: a bare name, or a name with its parameters.
 *
 * A bare name works when every param is optional.
 *
 * @example
 *
 * 'GetStores' // No params
 * 'GetList' // Optional queryParams
 * { name: 'GetList', queryParams: { pageSize: 10 } }
 * { name: 'GetStore', pathParams: { storeId } } // Required pathParams
 */
export type EndpointInput<E extends EndpointMap<E>> = EndpointInputs<
	E,
	EndpointParamKey
>;

/** Type the input for an endpoint URL, where a request body has no meaning. */
export type EndpointUrlInput<E extends EndpointMap<E>> = EndpointInputs<
	E,
	'pathParams' | 'queryParams'
>;

/** Get the response type for an endpoint name. */
export type EndpointResponse<
	E extends EndpointMap<E>,
	Name extends keyof E,
> = E[Name] extends { response: infer R } ? R : undefined;

/** Get the request body type for an endpoint name. */
export type EndpointBody<
	E extends EndpointMap<E>,
	Name extends keyof E,
> = E[Name] extends { body: infer B } ? B : never;

/** Get the endpoint name from an endpoint input. */
export type EndpointInputName<E extends EndpointMap<E>, T> = Extract<
	T extends { name: infer N } ? N : T,
	keyof E
>;

interface EndpointInputParts<Name> {
	body?: unknown;
	name: Name;
	pathParams?: Record<string, number | string>;
	queryParams?: URLParams;
}

function getInputParts<E extends EndpointMap<E>>(
	input: EndpointInput<E> | EndpointUrlInput<E>,
): EndpointInputParts<EndpointName<E>> {
	// The input types only hold valid names, but a generic union doesn't narrow.
	return (is.string(input) ? { name: input } : input) as EndpointInputParts<
		EndpointName<E>
	>;
}

/** Build the URL for an endpoint. */
export function buildEndpointUrl<E extends EndpointMap<E>>(
	baseUrl: string,
	definitions: EndpointDefinitions<E>,
	input: EndpointUrlInput<E>,
): string {
	const { name, pathParams, queryParams } = getInputParts<E>(input);
	return createUrl(
		joinPath(
			baseUrl,
			formatTemplate(
				definitions[name].path,
				mapValues(pathParams ?? {}, (value) => encodeURIComponent(value)),
			),
		),
		queryParams ?? {},
	);
}

/**
 * Build fetch options for an endpoint.
 *
 * The body is passed on as it is, the fetch layer stringifies JSON values.
 */
export function buildEndpointRequestOptions<E extends EndpointMap<E>>(
	definitions: EndpointDefinitions<E>,
	input: EndpointInput<E>,
): RequestOptions {
	const { body, name } = getInputParts<E>(input);
	const { allowEmpty, method } = definitions[name];
	return {
		allowEmpty,
		body: body as RequestOptions['body'],
		method,
	};
}

/**
 * Identify an endpoint request in the SWR cache.
 *
 * `T` is the input the key was built from and only exists in the type, so
 * parsing a key gives back the same input type.
 */
export type EndpointKey<T = unknown> = Branded<string, 'EndpointKey'> & {
	readonly __input?: T;
};

interface EndpointKeyData {
	__ENDPOINT_PATH?: string;
	body?: unknown;
	name: string;
	pathParams?: unknown;
	queryParams?: unknown;
}

/**
 * Build an SWR key for an endpoint.
 *
 * The endpoint URL is not enough as the key for all endpoints since some have
 * a body that changes while the URL stays the same.
 */
export function buildEndpointKey<
	E extends EndpointMap<E>,
	T extends EndpointInput<E> = EndpointInput<E>,
>(definitions: EndpointDefinitions<E>, input: T): EndpointKey<T> {
	const { body, name, pathParams, queryParams } = getInputParts<E>(input);
	return JSON.stringify({
		__ENDPOINT_PATH: definitions[name].path,
		name,
		pathParams,
		queryParams,
		body,
	} satisfies EndpointKeyData) as EndpointKey<T>;
}

/**
 * Parse an SWR key for an endpoint.
 *
 * Throws for a key whose path has since changed or whose endpoint no longer
 * exists.
 *
 * @see buildEndpointKey()
 */
export function parseEndpointKey<
	E extends EndpointMap<E>,
	T extends EndpointInput<E> = EndpointInput<E>,
>(definitions: EndpointDefinitions<E>, key: EndpointKey<T>): T {
	const data = JSON.parse(key) as EndpointKeyData;
	if (
		!Object.hasOwn(definitions, data.name) ||
		data.__ENDPOINT_PATH !== definitions[data.name as keyof E].path
	) {
		throw new Error(`parseEndpointKey: outdated key for ${data.name}`);
	}
	delete data.__ENDPOINT_PATH;
	// A name-only object and a bare name give the same key, so an input like
	// `{ name: 'GetList' }` comes back as `'GetList'` and doesn't match `T`.
	// Fine for now since the fetch functions take both forms.
	return (Object.keys(data).length === 1 ? data.name : data) as T;
}
